import { useLazyQuery } from '@apollo/client';
import { Button, Form, Input, message, Modal, Result } from 'antd';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useContext, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { AppContext } from '../../../AppContext';
import { LOCAL_STORAGE, REGEX, TIMER } from '../../../common/constants';
import {
  getItem,
  getNumberAndCode,
  removeItem,
  setItem,
} from '../../../common/utils';
import { customizeRequiredMark } from '../../../components/CustomRequiredMark';
import ResendOtp from '../../../components/ResendOtpButton';
import { AppContextType } from '../../../types/appContext.type';
import { ModalType } from '../../../types/common.type';
import { initiate } from '../../auth/OtpLess';
import '../../profile/profile.less';
import { PHONE_NUMBER_EXIST } from '../graphql/query';

const ChangeNumber = ({ onClose, visible, updateMobileNumber }: ModalType) => {
  const [profileForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>('');
  const [step, setStep] = useState<number>(() => {
    const currentPhoneNumber = getItem(LOCAL_STORAGE.CURRENT_PHONE);
    if (!currentPhoneNumber) return 1;
    const storedTimer = parseInt(getItem(LOCAL_STORAGE.OTP_TIMER) || '0', 10);
    return storedTimer > 0 ? 2 : 1;
  });
  const [timer, setTimer] = useState<number>(() => {
    const currentPhoneNumber = getItem(LOCAL_STORAGE.CURRENT_PHONE);
    if (!currentPhoneNumber) return 0;

    let otpAttempts = parseInt(getItem(LOCAL_STORAGE.OTP_ATTEMPTS) || '1', 10);
    if (otpAttempts > 2) {
      otpAttempts = 0;
      setItem(LOCAL_STORAGE.OTP_ATTEMPTS, '0');
    }
    const initialTime =
      otpAttempts === 2
        ? TIMER.THIRD_TIME_RESEND_OTP_SECONDS
        : otpAttempts === 1
          ? TIMER.SECOND_TIME_RESEND_OTP_SECONDS
          : TIMER.INITIAL_TIME_SECONDS;

    const storedTime = parseInt(
      getItem(LOCAL_STORAGE.OTP_TIMER) || initialTime.toString(),
      10,
    );
    return !isNaN(storedTime) && storedTime > 0 ? storedTime : initialTime;
  });

  const {
    state: { currentUser },
  } = useContext(AppContext) as unknown as AppContextType;

  const [phoneNumber, setPhoneNumber] = useState(() => {
    return (
      getItem(LOCAL_STORAGE.LAST_PHONE_NUMBER) ||
      `${currentUser?.countryCode}${currentUser?.phoneNo}`
    );
  });

  const [isChangeNumberModalOpen, setIsChangeNumberModalOpen] =
    useState<boolean>(false);

  const [getPhoneNoExist] = useLazyQuery(PHONE_NUMBER_EXIST, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    setItem(LOCAL_STORAGE.OTP_STEP, step.toString());
  }, [step]);

  useEffect(() => {
    if (!visible) {
      removeItem(LOCAL_STORAGE.OTP_TIMER);
    }
  }, [visible]);

  // Persistent Timer Countdown
  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            removeItem(LOCAL_STORAGE.OTP_TIMER);
          }
          const newTimer = prev - 1;
          setItem(LOCAL_STORAGE.OTP_TIMER, newTimer.toString());
          return newTimer;
        });
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleNextStep = async (value: { phoneNo: string }) => {
    setLoading(true);
    const storedBlockTime = localStorage.getItem(LOCAL_STORAGE.OTP_BLOCK_TIME);
    const currentTime = Date.now();

    if (storedBlockTime && parseInt(storedBlockTime) > currentTime) {
      message.error(`Please wait for 10 minutes before trying again.`);
      setIsChangeNumberModalOpen(false);
      onClose();
      setStep(1);
      setLoading(false);
      return;
    }

    localStorage.removeItem(LOCAL_STORAGE.OTP_TIMER);

    try {
      const phoneNumber = `+${value?.phoneNo?.replace(/\D/g, '')}`;
      const isNewNumber = parsePhoneNumber(phoneNumber);

      if (
        phoneNumber === `+${currentUser?.countryCode}${currentUser?.phoneNo}`
      ) {
        message.error(
          `You are currently logged in with this phone number, please enter a new one.`,
        );
        setIsChangeNumberModalOpen(false);
        setStep(1);
        onClose();
        return;
      }
      const storedPhoneNumber = getItem(LOCAL_STORAGE.CURRENT_PHONE);
      if (storedPhoneNumber && storedPhoneNumber !== phoneNumber) {
        setItem(LOCAL_STORAGE.OTP_ATTEMPTS, '0');
      }

      const { data } = await getPhoneNoExist({
        variables: {
          data: {
            countryCode: isNewNumber?.countryCallingCode,
            phoneNo: isNewNumber?.nationalNumber,
          },
        },
        fetchPolicy: 'network-only',
      });

      if (data?.isPhoneNoExistWeb?.phoneNoExist) {
        message.error('Phone Number already exists.');
        setPhoneNumber(`+${currentUser?.countryCode}${currentUser?.phoneNo}`);
        setIsChangeNumberModalOpen(isChangeNumberModalOpen);
        onClose();
        setStep(1);
        return;
      }

      setPhoneNumber(phoneNumber);
      setItem(LOCAL_STORAGE.CURRENT_PHONE, phoneNumber);
      await initiate(
        isNewNumber?.nationalNumber,
        isNewNumber?.countryCallingCode,
        TIMER.BLOCK_DURATION_SECONDS,
      );
      setTimer(TIMER.INITIAL_TIME_SECONDS);
      setItem(LOCAL_STORAGE.OTP_TIMER, TIMER.INITIAL_TIME_SECONDS.toString());
      if (LOCAL_STORAGE.OTP_ATTEMPTS > '2') {
        removeItem(LOCAL_STORAGE.OTP_ATTEMPTS);
        removeItem(LOCAL_STORAGE.OTP_BLOCK_TIME);
      }
      setStep(2);
    } catch (_E) {
      message.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (value: { otp: string; phoneNo: string }) => {
    const phoneNumber = `+${value?.phoneNo?.replace(/\D/g, '')}`;
    // COMMENTING NEXT FEW LINES DUE TO ISSUE IN OTPLESS, WILL BE USED LATER
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isNewNumber = parsePhoneNumber(phoneNumber);
    setLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const otpResponse: any = {};
      // const otpResponse: OTPLessResponse = await verify(
      //   isNewNumber?.nationalNumber,
      //   value?.otp,
      //   isNewNumber?.countryCallingCode,
      // );
      if (otpResponse?.response?.errorMessage) {
        setLoading(false);
        message.error('Incorrect OTP. Please try again.');
        setOtp('');
        profileForm.resetFields(['otp']);
      } else {
        setLoading(false);
        setStep(3);
        removeItem(LOCAL_STORAGE.OTP_STEP);
        removeItem(LOCAL_STORAGE.CURRENT_PHONE);
        removeItem(LOCAL_STORAGE.OTP_ATTEMPTS);
        removeItem(LOCAL_STORAGE.OTP_BLOCK_TIME);
      }
    } catch (_E) {
      //
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatedPhoneNumber = async () => {
    profileForm.setFieldsValue({ phoneNo: phoneNumber });
    const currNumber = getNumberAndCode(phoneNumber);

    if (updateMobileNumber) {
      updateMobileNumber(currNumber?.nationalNumber.toString());
    }

    setIsChangeNumberModalOpen(false);
    setStep(1);
    setLoading(false);
    setOtp('');
    onClose();
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setPhoneNumber('');
    removeItem(LOCAL_STORAGE.CURRENT_PHONE);
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        profileForm.setFieldsValue({ phoneNo: '' });
        setPhoneNumber('');
        onClose();
      }}
      footer={null}
      centered
      className="change-number-modal"
      title={
        <div className="body-lg semibold">
          {step === 3
            ? 'Phone Number Updated Successfully'
            : 'Update Phone Number'}
        </div>
      }
    >
      {step === 1 ? (
        <Form
          form={profileForm}
          layout="vertical"
          requiredMark={customizeRequiredMark}
          initialValues={{ phoneNumber: phoneNumber }}
          onFinish={() => handleNextStep({ phoneNo: phoneNumber })}
        >
          <Form.Item
            label={<span className="body-meta medium">Phone Number</span>}
            name="phoneNumber"
            rules={[
              { required: true, message: 'Phone number is required' },
              {
                pattern: REGEX.PHONE,
                message: 'Please enter a valid phone number',
              },
            ]}
          >
            <PhoneInput
              value={phoneNumber}
              inputProps={{
                autoFocus: true,
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    profileForm.submit();
                  }
                },
              }}
              onChange={(value) => setPhoneNumber(value)}
              enableSearch={true}
            />
          </Form.Item>
          <Button
            className="custom-button body-meta medium"
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Get OTP
          </Button>
        </Form>
      ) : step === 2 ? (
        <Form
          form={profileForm}
          layout="vertical"
          requiredMark={customizeRequiredMark}
          onFinish={() => handleOtpVerify({ otp, phoneNo: phoneNumber })}
        >
          <div className="otp-sent-text">
            <span className="body-meta medium">
              OTP sent to: <strong>{phoneNumber}</strong>
            </span>
          </div>
          <Form.Item
            name="otp"
            label={<span className="body-meta medium">Enter code</span>}
            rules={[
              { required: true, message: 'Please Enter your OTP!' },
              {
                pattern: REGEX.NUMBER,
                message: 'OTP can only contain numbers!',
              },
            ]}
          >
            <Input
              maxLength={6}
              className="custom-input"
              placeholder="000000"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
              }}
            />
          </Form.Item>
          <Button
            className="change-number-btn"
            type="link"
            onClick={handleBackToStep1}
          >
            Change Number
          </Button>
          <div className="resend-otp-container">
            {timer > 0 ? (
              <Button
                className="otp-timer-button"
                type="text"
                disabled={timer > 0}
              >
                Resend OTP in {timer} seconds
              </Button>
            ) : (
              <ResendOtp phoneNumber={phoneNumber} />
            )}
          </div>

          <Button
            className="verify-btn body-meta medium"
            type="primary"
            loading={loading}
            htmlType="submit"
          >
            Verify OTP
          </Button>
        </Form>
      ) : (
        <Result
          className="success-screen"
          status="success"
          extra={[
            <Button
              key="got-it"
              type="primary"
              onClick={handleUpdatedPhoneNumber}
              className="got-it-btn"
            >
              Got It
            </Button>,
          ]}
        />
      )}
    </Modal>
  );
};

export default ChangeNumber;
